import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, Send, Users, Radio, Tv, X, Eye,
} from "lucide-react";

import { toast } from "sonner";

const PLATFORM_COLORS = {
  youtube: "bg-red-500",
  tiktok: "bg-black",
  facebook: "bg-blue-600",
  twitch: "bg-purple-600",
  other: "bg-[#FF6B35]",
};

export default function LivePage() {
  const { id } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [live, setLive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const chatEndRef = useRef(null);
  const pollRef = useRef(null);
  const lastMsgTime = useRef(null);

  const fetchLive = useCallback(async () => {
    try {
      const res = await api.get(`/lives/${id}`);
      setLive(res.data.live);
    } catch {
      navigate("/discover");
    }
  }, [api, id, navigate]);

  const fetchChat = useCallback(async () => {
    try {
      const params = { limit: 200 };
      if (lastMsgTime.current) params.after = lastMsgTime.current;
      const res = await api.get(`/lives/${id}/chat`, { params });
      if (res.data.messages.length > 0) {
        if (lastMsgTime.current) {
          setMessages((prev) => [...prev, ...res.data.messages]);
        } else {
          setMessages(res.data.messages);
        }
        lastMsgTime.current = res.data.messages[res.data.messages.length - 1].created_at;
      }
    } catch (err) { console.error(err); }
  }, [api, id]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchLive(), fetchChat()]).finally(() => setLoading(false));
    pollRef.current = setInterval(fetchChat, 3000);
    return () => clearInterval(pollRef.current);
  }, [fetchLive, fetchChat]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!newMsg.trim() || sending) return;
    setSending(true);
    try {
      const res = await api.post(`/lives/${id}/chat`, { content: newMsg });
      setMessages((prev) => [...prev, res.data.message]);
      lastMsgTime.current = res.data.message.created_at;
      setNewMsg("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      const videoId = url.includes("youtu.be")
        ? url.split("/").pop()?.split("?")[0]
        : new URLSearchParams(new URL(url).search).get("v");
      return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : null;
    }
    if (url.includes("facebook.com")) {
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&autoplay=true`;
    }
    if (url.includes("twitch.tv")) {
      const channel = url.split("/").pop();
      return `https://player.twitch.tv/?channel=${channel}&parent=${window.location.hostname}`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="py-6 space-y-4">
        <div className="bg-foreground/5 rounded-3xl h-64 animate-pulse" />
      </div>
    );
  }

  if (!live) return null;

  const embedUrl = getEmbedUrl(live.embed_url);
  const isCreator = live.creator_user_id === user?.id;

  const handleEndLive = async () => {
    if (!window.confirm("Terminer le live ?")) return;
    try {
      await api.post(`/lives/${id}/end`);
      navigate("/discover");
    } catch (err) { console.error(err); }
  };

  return (
    <div className="py-4 flex flex-col h-[calc(100vh-120px)]" data-testid="live-page">
      {/* Header */}
      <div className="flex items-center gap-3 pb-3">
        <button
          onClick={() => navigate(-1)}
          className="p-1.5 rounded-xl hover:bg-foreground/5 text-muted-foreground"
          data-testid="live-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 flex-1">
          <div className={`w-2 h-2 rounded-full ${live.is_active ? "bg-red-500 animate-pulse" : "bg-[#6E6680]"}`} />
          <span className="font-heading font-bold text-sm text-foreground truncate">{live.title}</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full text-white ${PLATFORM_COLORS[live.platform] || PLATFORM_COLORS.other}`}>
            {live.platform}
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Eye className="w-3 h-3" />
          {live.viewer_count}
        </div>
        {isCreator && live.is_active && (
          <button
            onClick={handleEndLive}
            className="text-xs bg-red-500/20 text-red-400 px-3 py-1 rounded-full hover:bg-red-500/30 transition-colors"
            data-testid="live-end-btn"
          >
            Terminer
          </button>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:flex-row gap-4 min-h-0">
        {/* Video embed */}
        <div className="lg:flex-1 shrink-0">
          {embedUrl ? (
            <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black">
              <iframe
                src={embedUrl}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                title="Live"
              />
            </div>
          ) : (
            <div className="w-full aspect-video rounded-2xl bg-foreground/5 border border-foreground/10 flex flex-col items-center justify-center gap-2">
              <Tv className="w-10 h-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Live en cours sur {live.platform}</p>
              <a
                href={live.embed_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#FF6B35] hover:underline"
              >
                Ouvrir le lien
              </a>
            </div>
          )}

          {/* Artist info */}
          <div className="flex items-center gap-3 mt-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center">
              {live.artist_avatar ? (
                <img src={live.artist_avatar} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-white text-xs font-bold">
                  {(live.artist_name || "?")[0].toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <p className="font-heading font-semibold text-sm text-foreground">{live.artist_name}</p>
              <p className="text-[10px] text-muted-foreground">
                {live.is_active ? "En direct" : "Live termine"}
              </p>
            </div>
          </div>
        </div>

        {/* Chat */}
        <div className="lg:w-80 flex flex-col bg-card border border-border rounded-2xl overflow-hidden min-h-[200px] lg:min-h-0 shadow-sm">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
            <Radio className="w-3.5 h-3.5 text-red-500" />
            <span className="font-heading font-bold text-xs text-card-foreground">Chat en direct</span>
            <span className="text-[10px] text-muted-foreground ml-auto">{messages.length} msg</span>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1 scrollbar-hide" data-testid="live-chat-messages">
            {messages.map((m) => (
              <div key={m.id} className="flex items-start gap-2 py-0.5" data-testid={`live-msg-${m.id}`}>
                <span className={`text-xs font-bold shrink-0 ${
                  m.user_role === "artist" ? "text-[#FF6B35]" : "text-[#FF8FA3]"
                }`}>
                  {m.user_pseudo}
                </span>
                <span className="text-xs text-card-foreground/80 break-words">{m.content}</span>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          {live.is_active && (
            <div className="px-3 py-2 border-t border-border">
              <div className="flex gap-1.5">
                <input
                  placeholder="Envoyer un message..."
                  value={newMsg}
                  onChange={(e) => setNewMsg(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 bg-white border border-border rounded-full px-3 py-1.5 text-xs text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] outline-none"
                  data-testid="live-chat-input"
                  maxLength={300}
                />
                <button
                  onClick={handleSend}
                  disabled={sending || !newMsg.trim()}
                  className="bg-[#FF6B35] text-white p-1.5 rounded-full hover:scale-105 active:scale-95 transition-transform disabled:opacity-50 shrink-0"
                  data-testid="live-chat-send"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1 text-center">+1 credit par message</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
